import * as React from 'react';

import * as Yup from 'yup';
import {
	Field, FieldProps,
} from 'formik';
import Modal from 'antd/es/modal';

import { Chat, ChatKind } from '@common/react/components/Chat/Chat';
import Button from '@common/react/components/Forms/Button';
import { BaseParams } from '@common/react/objects/BaseParams';
import { getUserName } from '@common/react/utils/utils';
import { useChatSettingsProviderContext } from '@common/react/components/Chat/ChatSettingsProvider';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';
import { FormikFieldUserList } from '@common/react/components/Chat/AddChatPlugin/FormikFieldUserList';
import { BaseUserWithAvatarWithDeleted } from '@common/typescript/objects/BaseUser';

const validationSchema = Yup.object().shape({
	kind: Yup.number(),
	contacts: Yup.array().when('kind', {
		is: ChatKind.Group,
		then: Yup.array().min(1, 'You must select users'),
	}),
});

interface AddChatPopupProps {
	user: BaseUserWithAvatarWithDeleted;
	onSave: (chat: Chat) => void;
	render: (show: () => void) => void;
	autocompleteParams?: BaseParams;
	type?: string;
	renderItem?: (user: BaseUserWithAvatarWithDeleted) => React.ReactNode;
	setChatKind?: (contacts: Array<any>, kind: ChatKind) => ChatKind;
}

interface AddChatPopupState {
	isLoading: boolean;
	popupShow: boolean;
}

interface FormValues {
	id: number;
	name: string;
	kind: ChatKind;
	contacts: Array<BaseUserWithAvatarWithDeleted>;
}

const AddChatPopup: React.FC<AddChatPopupProps> = (props) => {
	const [state, setState] = React.useState<AddChatPopupState>({
		isLoading: false,
		popupShow: false,
	});

	const { state: { requests } } = useChatSettingsProviderContext();

	const showPopup = () => setState((prev) => ({ ...prev, popupShow: true }));
	const popupHide = () => setState((prev) => ({ ...prev, popupShow: false }));

	const {
		user: loggedUser, autocompleteParams, type = 'userList', renderItem = (user: BaseUserWithAvatarWithDeleted) => getUserName(user),
	} = props;

	return <>
		{props.render(showPopup)}
		<Modal
			title="New Chat"
			open={state.popupShow}
			onCancel={popupHide}
			destroyOnClose
			width={500}
			footer={null}
		>
			<ItemProvider<FormValues>
				id={-1}
				add={{
					id: -1,
					name: '',
					kind: ChatKind.Group,
					contacts: [],
				} as FormValues}
				validationSchema={validationSchema}
				saveRequest={requests.chat}
				clearForSubmit={({ item }) => {
					return {
						kind: props.setChatKind ? props.setChatKind(item.contacts, item.kind) : item.kind,
						name: item.name.trim() === '' ? '' : item.name,
						contacts: item.contacts.map((contact) => (
							{
								id: -1,
								userId: contact.id,
							}
						)).concat(
							{
								id: -1,
								userId: props.user.id,
							},
						),
					};
				}}
			>
				<ItemEditor<FormValues, Chat>
					afterSubmit={({ res }) => {
						popupHide();
						props.onSave?.(res);
					}}
					edit={({ formikBag, state }) => {
						return <>
							<div className="row">
								{formikBag.values.kind !== ChatKind.Personal
									&& <Field name="name">
										{({ field }: FieldProps<string, FormValues>) =>
											<div className="form-group col-sm-6">
												<label htmlFor="chatName">Name</label>
												<div className="is-relative">
													<input type="text" className="form-control" {...field} />
												</div>
											</div>
										}
									</Field>
								}
							</div>
							<div className="row">
								<FormikFieldUserList<BaseUserWithAvatarWithDeleted, FormValues>
									fieldName="contacts"
									label="Contacts*"
									className={`form-group col-sm-12 ${formikBag.errors.contacts && formikBag.touched.contacts ? 'has-error' : ''}`}
									renderItem={renderItem}
									filterComponentProps={{
										params: {
											...autocompleteParams,
											excludeUserIds: [loggedUser.id].concat(
												formikBag.values.contacts?.filter((u) => !u.deleted)?.map((user) => user.id),
											),
										},
										antdProps: {
											filterOption: (inputValue, option: any) => {
												const id = +(option.key as string);
												return loggedUser && loggedUser.id !== id
													&& !formikBag.values.contacts.some((item) => id === item.id && !item.deleted);
											},
										},
									}}
								/>
							</div>
							<div className="text-right">
								<Button isLoading={state.loading} className="btn btn-sm btn-primary">Save</Button>
							</div>
						</>;
					}}
				/>
			</ItemProvider>
		</Modal>
	</>;
};

export default AddChatPopup;
